import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { BookOpen, ChevronRight, ArrowLeft, Check } from 'lucide-react';
import LanguageFlag from '../components/LanguageFlag';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function SeriesDetail() {
  const navigate = useNavigate();
  const { seriesId } = useParams();
  const [series, setSeries] = useState(null);
  const [episodes, setEpisodes] = useState([]);
  const [progress, setProgress] = useState({});
  const [loading, setLoading] = useState(true);
  const [opening, setOpening] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/series/${seriesId}`, {
          withCredentials: true,
        });
        setSeries(res.data.series);
        setEpisodes(res.data.episodes || []);

        // Forge series: fetch progress so episodes can show "completed"
        if (res.data.series?.source === 'forge_stories') {
          try {
            const prog = await axios.get(
              `${BACKEND_URL}/api/forge-stories/progress/${res.data.series.language_code}`,
              { withCredentials: true }
            );
            const map = {};
            (prog.data?.progress || []).forEach((p) => {
              if (p.episode != null) map[p.episode] = p;
            });
            setProgress(map);
          } catch {
            /* optional */
          }
        }
      } catch (err) {
        console.error('Failed to load series:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, [seriesId]);

  const openEpisode = async (ep) => {
    if (series?.source === 'forge_stories') {
      navigate(`/forge-stories/${series.language_code}/read/${ep.episode_number}`);
      return;
    }
    try {
      setOpening(ep.episode_id);
      const res = await axios.post(
        `${BACKEND_URL}/api/series/${seriesId}/episodes/${ep.episode_id}/open`,
        {},
        { withCredentials: true }
      );
      if (res.data?.text_id) {
        navigate(`/reader/${res.data.text_id}`);
      }
    } catch (err) {
      console.error('Failed to open episode:', err);
    } finally {
      setOpening(null);
    }
  };

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto p-4 space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-32 w-full" />
        {[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-16 w-full" />)}
      </div>
    );
  }

  if (!series) {
    return (
      <div className="max-w-3xl mx-auto p-6 text-center">
        <p className="text-muted-foreground">Series not found.</p>
        <Button onClick={() => navigate('/series')} className="mt-4">Back to Series</Button>
      </div>
    );
  }

  const completedCount = Object.values(progress).filter((p) => p.completed).length;
  const progressPct = episodes.length ? (completedCount / episodes.length) * 100 : 0;

  return (
    <div className="min-h-screen p-4 md:p-6" data-testid="series-detail-page">
      <div className="max-w-3xl mx-auto space-y-6">
        <div className="flex items-start gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/series')} data-testid="series-detail-back">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-1">
              <span className="text-3xl" aria-hidden>{series.cover_emoji || '📖'}</span>
              <h1 className="text-2xl md:text-3xl font-bold">{series.title}</h1>
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <LanguageFlag code={series.language_code} size="sm" />
              <span>{series.language_name || series.language_code}</span>
              {series.cefr_level && <Badge variant="outline" className="text-[10px]">{series.cefr_level}</Badge>}
              {series.theme && <span>· {series.theme}</span>}
            </div>
          </div>
        </div>

        <Card>
          <CardContent className="p-5 space-y-3">
            <p className="text-sm leading-relaxed">{series.description}</p>
            {series.source === 'forge_stories' && episodes.length > 0 && (
              <div>
                <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                  <span>{completedCount} / {episodes.length} episodes</span>
                  <span>{Math.round(progressPct)}%</span>
                </div>
                <div className="h-2 rounded-full bg-muted overflow-hidden">
                  <div className="h-full bg-primary transition-all" style={{ width: `${progressPct}%` }} />
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="space-y-2">
          {episodes.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                <BookOpen className="h-10 w-10 mx-auto mb-3 opacity-30" />
                <p>No episodes yet — check back soon.</p>
              </CardContent>
            </Card>
          ) : (
            episodes.map((ep) => {
              const done = progress[ep.episode_number]?.completed;
              return (
                <Card
                  key={ep.episode_id}
                  className="hover:shadow-md transition-all cursor-pointer group"
                  onClick={() => openEpisode(ep)}
                  data-testid={`series-episode-${ep.episode_number || ep.episode_id}`}
                >
                  <CardContent className="p-4 flex items-center gap-4">
                    <div
                      className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm ${
                        done
                          ? 'bg-green-500 text-white'
                          : 'bg-muted text-muted-foreground group-hover:bg-primary group-hover:text-primary-foreground'
                      }`}
                    >
                      {done ? <Check className="h-4 w-4" /> : ep.episode_number}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium truncate group-hover:text-primary transition-colors">
                        {ep.title || `Episode ${ep.episode_number}`}
                      </h4>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground">
                        {ep.word_count ? <span>{ep.word_count} words</span> : null}
                        {ep.cefr_level && <span>· {ep.cefr_level}</span>}
                      </div>
                    </div>
                    <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary" />
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
